import {
	AnswerReveal,
	Modal,
	PointsRow,
	StandardPatternBody,
} from '@quizzach/components';
import { IQuestion } from '@quizzach/interfaces';
import {
	analyzeVisualQuestion,
	patternAnswerHtml,
	visualAnswerHtml,
} from '@quizzach/utils';
import {
	OptList,
	QuestionText,
	QuestionContainer,
} from '@quizzach/play-styles';
import { Colors, Text, TextSize } from '@quizzach/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Expand, Xcollab } from '@quizzach/assets';
import { ImageCredits } from '@quizzach/layouts';

interface IVisualQuestionProps {
	color: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions?: number;
}
interface IVisualQuestionAnswerProps {
	questionContainerColor: string;
	answerContainerColor: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions?: number;
}

type VisualOption = 'a' | 'b' | 'c' | 'd';

const getAnswerOptions = (question: IQuestion): VisualOption[] | null => {
	const visualQuestion = question.visual_question;
	if (!visualQuestion?.a || !visualQuestion?.b || !visualQuestion?.c) {
		return null;
	}
	return visualQuestion.d ? ['a', 'b', 'c', 'd'] : ['a', 'b', 'c'];
};

/* Same look as the abcd question type; lives outside the pattern/plain
   switch so options survive a pattern-styled question text. */
const VisualOptions = ({ question }: { question: IQuestion }) => {
	const options = getAnswerOptions(question);
	if (!options) return null;

	return (
		<OptList>
			{options.map((option) => (
				<div className='opt' key={option}>
					<span className='L'>{option}</span>
					<span
						className='t'
						dangerouslySetInnerHTML={{
							__html: question.visual_question?.[option] || '',
						}}
					/>
				</div>
			))}
		</OptList>
	);
};

/* Answer page shows the alternative (e.g. "revealed") image when uploaded. */
const visualImageFilename = (question: IQuestion, answer = false) =>
	(answer && question.visual_question?.answer_filename) ||
	question.visual_question?.filename ||
	'';

const visualImageUrl = (filename: string) =>
	`${import.meta.env.VITE_ASSETS_URL}/visual_questions/${filename}`;

/* Shared image panel with skeleton, expand modal and credits. */
const useVisualImage = (filename: string) => {
	const [isImgLoading, setIsImgLoading] = useState(true);
	const loadStartTime = useRef<number>(Date.now());
	const timeoutRef = useRef<number>();

	useEffect(() => {
		setIsImgLoading(true);
		loadStartTime.current = Date.now();
	}, [filename]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const handleImageLoad = () => {
		const elapsed = Date.now() - loadStartTime.current;
		const remainingTime = 200 - elapsed;

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		if (remainingTime > 0) {
			timeoutRef.current = window.setTimeout(
				() => setIsImgLoading(false),
				remainingTime
			);
		} else {
			setIsImgLoading(false);
		}
	};

	return { isImgLoading, handleImageLoad };
};

const VisualImagePanel = ({
	currentQuestion,
	filename,
	isImgLoading,
	handleImageLoad,
	onExpand,
}: {
	currentQuestion: IQuestion;
	filename: string;
	isImgLoading: boolean;
	handleImageLoad: () => void;
	onExpand: () => void;
}) => {
	const { t } = useTranslation();
	const imgRef = useRef<HTMLImageElement>(null);
	const [captionWidth, setCaptionWidth] = useState<number>();

	/* The caption row must line up with the photo's edges, but the rendered
	   photo width is height-constrained and unknowable in CSS — measure it. */
	useEffect(() => {
		const img = imgRef.current;
		if (isImgLoading || !img) return;
		const measure = () =>
			setCaptionWidth(img.clientWidth > 0 ? img.clientWidth : undefined);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(img);
		return () => observer.disconnect();
	}, [isImgLoading]);

	return (
		<div className='img-wrapper'>
			{isImgLoading && <div className='loading-skeleton'></div>}

			<motion.figure
				initial={{ opacity: 0 }}
				animate={{ opacity: isImgLoading ? 0 : 1 }}
				transition={{ duration: 0.5 }}
				onClick={onExpand}
			>
				<img
					ref={imgRef}
					src={visualImageUrl(filename)}
					alt='visual-question-img'
					onLoad={handleImageLoad}
					style={{ display: isImgLoading ? 'none' : 'block' }}
				/>
				{!isImgLoading && (
					<figcaption className='img-meta' style={{ width: captionWidth }}>
						{currentQuestion.visual_question?.licence_needed && (
							<ImageCredits currentQuestion={currentQuestion} />
						)}
						<span className='img-expand-action'>
							<Text
								$size={TextSize.xxs}
								color={Colors.textMuted}
								style={{ margin: 0 }}
							>
								{t('common.imageCredits.expand')}
							</Text>
							<Expand size={11} color={Colors.textMuted} />
						</span>
					</figcaption>
				)}
			</motion.figure>
		</div>
	);
};

const VisualExpandModal = ({
	filename,
	closeModal,
}: {
	filename: string;
	closeModal: () => void;
}) => (
	<Modal closeModal={closeModal} visual>
		<img
			src={visualImageUrl(filename)}
			alt='visual-question-img'
			style={{
				maxWidth: '90vw',
				maxHeight: '90vh',
				borderRadius: '16px',
			}}
		/>
		<div className='img-close-icon' onClick={closeModal}>
			<Xcollab size={20} color={Colors.brandMain} />
		</div>
	</Modal>
);

export const VisualQuestionDesktop = (props: IVisualQuestionProps) => {
	const { color, currentQuestion } = props;
	const [expandImage, setExpandImage] = useState(false);
	const filename = visualImageFilename(currentQuestion);
	const { isImgLoading, handleImageLoad } = useVisualImage(filename);
	const analysis = useMemo(
		() => analyzeVisualQuestion(currentQuestion),
		[currentQuestion]
	);

	return (
		<>
			{expandImage && (
				<VisualExpandModal
					filename={filename}
					closeModal={() => setExpandImage(false)}
				/>
			)}
			<div className='visual'>
				<div className='visual-text'>
					{analysis.pattern || analysis.note || analysis.kicker ? (
						<StandardPatternBody analysis={analysis} color={color} />
					) : (
						<QuestionContainer color={color} style={{ width: '100%' }}>
							<QuestionText>
								<div
									dangerouslySetInnerHTML={{ __html: analysis.questionHtml }}
								/>
							</QuestionText>
						</QuestionContainer>
					)}
					<VisualOptions question={currentQuestion} />
					<PointsRow
						points={currentQuestion.points}
						explanation={currentQuestion.points_explanation}
					/>
				</div>
				<VisualImagePanel
					currentQuestion={currentQuestion}
					filename={filename}
					isImgLoading={isImgLoading}
					handleImageLoad={handleImageLoad}
					onExpand={() => setExpandImage(true)}
				/>
			</div>
		</>
	);
};

export const VisualQuestionAnswerDesktop = (
	props: IVisualQuestionAnswerProps
) => {
	const { questionContainerColor, currentQuestionNumber, currentQuestion } =
		props;
	const [expandImage, setExpandImage] = useState(false);
	const filename = visualImageFilename(currentQuestion, true);
	const { isImgLoading, handleImageLoad } = useVisualImage(filename);
	const analysis = useMemo(
		() => analyzeVisualQuestion(currentQuestion),
		[currentQuestion]
	);

	return (
		<>
			{expandImage && (
				<VisualExpandModal
					filename={filename}
					closeModal={() => setExpandImage(false)}
				/>
			)}
			<div className='visual visual-answer'>
				<div className='visual-text'>
					{analysis.pattern || analysis.note || analysis.kicker ? (
						<StandardPatternBody
							analysis={analysis}
							color={questionContainerColor}
							dim
						/>
					) : (
						<QuestionContainer
							color={questionContainerColor}
							style={{ width: '100%' }}
						>
							<QuestionText $small>
								<div
									dangerouslySetInnerHTML={{ __html: analysis.questionHtml }}
								/>
							</QuestionText>
						</QuestionContainer>
					)}
					<VisualOptions question={currentQuestion} />
				</div>
				<VisualImagePanel
					currentQuestion={currentQuestion}
					filename={filename}
					isImgLoading={isImgLoading}
					handleImageLoad={handleImageLoad}
					onExpand={() => setExpandImage(true)}
				/>
			</div>
			<AnswerReveal
				revealKey={currentQuestionNumber}
				answerHtml={patternAnswerHtml(
					analysis.pattern,
					visualAnswerHtml(
						currentQuestion.visual_question?.answer || '',
						currentQuestion.visual_question
					)
				)}
				explanation={currentQuestion.visual_question?.explanation}
			/>
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};
